// src/components/organizer/CommunicationsWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Button,
  Divider,
  Tabs,
  Tab,
  Badge,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as NotificationIcon,
  Campaign as CampaignIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  CheckCircle as SentIcon,
  Schedule as PendingIcon,
  Error as FailedIcon,
  Add as AddIcon
} from '@mui/icons-material';

const CommunicationsWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMessage, setNewMessage] = useState({
    type: 'email',
    recipient: 'all',
    subject: '',
    content: '',
    event: ''
  });

  const recentCommunications = [
    {
      id: 1,
      type: 'email',
      subject: 'Recordatorio: Maratón Ciudad - 1 semana',
      recipients: 450,
      event: 'Maratón Ciudad',
      status: 'sent',
      sentAt: '2024-12-09 10:30',
      openRate: 87
    },
    {
      id: 2,
      type: 'notification',
      subject: 'Cambio de horario - Copa Juvenil',
      recipients: 120,
      event: 'Copa Juvenil',
      status: 'sent',
      sentAt: '2024-12-09 09:15',
      openRate: 94
    },
    {
      id: 3,
      type: 'sms',
      subject: 'Confirmación de inscripción',
      recipients: 85,
      event: 'Triatlón Regional',
      status: 'pending',
      sentAt: '2024-12-09 08:00',
      openRate: 0
    }
  ];

  const communicationStats = {
    emailsSent: 1250,
    smssSent: 380,
    notificationsSent: 650,
    averageOpenRate: 89,
    averageResponseRate: 23
  };

  const getTypeIcon = (type) => {
    const icons = {
      email: <EmailIcon />,
      sms: <SmsIcon />,
      notification: <NotificationIcon />,
      campaign: <CampaignIcon />
    };
    return icons[type] || <EmailIcon />;
  };

  const getTypeColor = (type) => {
    const colors = {
      email: 'primary',
      sms: 'success',
      notification: 'info',
      campaign: 'warning'
    };
    return colors[type] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      sent: <SentIcon color="success" />,
      pending: <PendingIcon color="warning" />,
      failed: <FailedIcon color="error" />
    };
    return icons[status] || <PendingIcon />;
  };

  const getStatusLabel = (status) => {
    const labels = {
      sent: 'Enviado',
      pending: 'Pendiente',
      failed: 'Fallido'
    };
    return labels[status] || status;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSendMessage = () => {
    console.log('Enviando mensaje:', newMessage);
    setDialogOpen(false);
    setNewMessage({
      type: 'email',
      recipient: 'all',
      subject: '',
      content: '',
      event: ''
    });
  };

  return (
    <>
      <Card sx={{ height: '100%' }}>
        <CardHeader
          title="📨 Comunicaciones"
          subheader="Gestiona la comunicación con participantes"
          action={
            <Box display="flex" gap={1}>
              <Badge badgeContent={recentCommunications.filter(c => c.status === 'pending').length} color="warning">
                <PendingIcon />
              </Badge>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                onClick={() => setDialogOpen(true)}
              >
                Nuevo
              </Button>
            </Box>
          }
        />
        
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Recientes" />
            <Tab label="Estadísticas" />
          </Tabs>

          {/* Tab 1: Comunicaciones recientes */}
          {tabValue === 0 && (
            <Box>
              <List dense>
                {recentCommunications.map((comm) => (
                  <ListItem key={comm.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${getTypeColor(comm.type)}.main`, width: 32, height: 32 }}>
                        {React.cloneElement(getTypeIcon(comm.type), { fontSize: 'small' })}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight="bold">
                            {comm.subject}
                          </Typography>
                          <Box display="flex" gap={0.5}>
                            {getStatusIcon(comm.status)}
                            {comm.status === 'sent' && comm.openRate > 0 && (
                              <Chip
                                label={`${comm.openRate}% abierto`}
                                color="success"
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 16 }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            📧 {comm.recipients} destinatarios • {comm.event}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(comm.sentAt).toLocaleString('es-ES')} • {getStatusLabel(comm.status)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {/* Acciones rápidas */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ⚡ Plantillas Rápidas
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button variant="outlined" size="small" startIcon={<EmailIcon />}>
                    Recordatorio
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<NotificationIcon />}>
                    Actualización
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<CampaignIcon />}>
                    Promoción
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Tab 2: Estadísticas */}
          {tabValue === 1 && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h5" color="primary.main" fontWeight="bold">
                      {communicationStats.emailsSent}
                    </Typography>
                    <Typography variant="caption">Emails Enviados</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h5" color="success.main" fontWeight="bold">
                      {communicationStats.smssSent}
                    </Typography>
                    <Typography variant="caption">SMS Enviados</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h5" color="info.main" fontWeight="bold">
                      {communicationStats.notificationsSent}
                    </Typography>
                    <Typography variant="caption">Notificaciones</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="h5" color="warning.main" fontWeight="bold">
                      {communicationStats.averageOpenRate}%
                    </Typography>
                    <Typography variant="caption">Tasa Apertura</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Análisis de rendimiento */}
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📈 Rendimiento de Comunicaciones
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Mejor día para enviar: Martes (92% apertura)
                  <br />
                  • Mejor hora: 10:00 AM (89% apertura)
                  <br />
                  • Tipo más efectivo: Notificaciones push (94% apertura)
                  <br />
                  • Tasa de respuesta promedio: {communicationStats.averageResponseRate}%
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog para nueva comunicación */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          📨 Nueva Comunicación
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={newMessage.type}
                  label="Tipo"
                  onChange={(e) => setNewMessage({...newMessage, type: e.target.value})}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="notification">Notificación</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Destinatarios</InputLabel>
                <Select
                  value={newMessage.recipient}
                  label="Destinatarios"
                  onChange={(e) => setNewMessage({...newMessage, recipient: e.target.value})}
                >
                  <MenuItem value="all">Todos los participantes</MenuItem>
                  <MenuItem value="event">Participantes de un evento</MenuItem>
                  <MenuItem value="pending">Solo pendientes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Evento</InputLabel>
                <Select
                  value={newMessage.event}
                  label="Evento"
                  onChange={(e) => setNewMessage({...newMessage, event: e.target.value})}
                >
                  <MenuItem value="marathon">Maratón Ciudad</MenuItem>
                  <MenuItem value="copa">Copa Juvenil</MenuItem>
                  <MenuItem value="triathlon">Triatlón Regional</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Asunto"
                value={newMessage.subject}
                onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Mensaje"
                value={newMessage.content}
                onChange={(e) => setNewMessage({...newMessage, content: e.target.value})}
                placeholder="Escribe tu mensaje aquí..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSendMessage}
            startIcon={<SendIcon />}
            disabled={!newMessage.subject || !newMessage.content}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommunicationsWidget;